import type {CSSProperties} from 'react';
import React, { useEffect, useRef, useState} from 'react';
import {Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import type { ReduxStateSetterProps, ReduxTimeSetterProps, TrimData, TrimPanelBaseProps} from '@Panels/trim-panels/trim-panels.types';
import {REDUX_STATE, TIME_BLOCK_ALIGNMENT} from '@Panels/trim-panels/trim-panels.types';
import {TimeBlock} from '@Panels/trim-panels/components/time-block';
import {TrimRangeSlider} from '@Panels/trim-panels/components/trim-range-slider';
import ReactPlayer from 'react-player';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {TrimVideoReduxState} from '@Panels/trim-panels/trim-video-panel/trim-video-panel.types';
import {initializeState, setEndTime, setPlaying, setSeekingTime, setStartTime, togglePlaying} from '@Panels/trim-panels/trim-panels-reducer';
import type {OnProgressProps} from 'react-player/types/base';
import {TrimVideoProgress} from '@Panels/trim-panels/components/trim-video-progress';
import {TrimVideoThumbnailSprite} from '@Panels/trim-panels/components/trim-video-thumbnail-sprite';
import {v4 as uuidv4} from 'uuid';
import type { PanelMessageProp} from '@Components/panel/panel.types';
import {PANEL_MESSAGE_TYPE} from '@Components/panel/panel.types';
import {DOTS_COLOR, ThreeDotsLoader} from '@Components/three-dots-loader/three-dots-loader';
import {getUniqueString} from '@Utils/string.util';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import {closePanel} from '@Components/panel/panel-reducer';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import { useContainerQuery} from '@Hooks/useContainerQuery';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './trim-video-panel.module.scss';

export interface TrimVideoPanelProps extends TrimPanelBaseProps {
  isMuted: boolean;
  scaledWidth: number;
  scaledHeight: number;
}

const query: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 550,
  },
  large: {
    minWidth: 551,
  },
};

const MAX_DURATION_EXCEEDED_ERROR_PROP: PanelMessageProp = {
  type: PANEL_MESSAGE_TYPE.ERROR,
  message: window.i18next.t('pmwjs_maximum_video_duration'),
};

const MAX_GAP = 600;
const MIN_GAP = 1;
const TRIM_REDUX_HASHMAP_KEY: REDUX_STATE = REDUX_STATE.TYPE_VIDEO;
const VIDEO_THUMBNAIL_SPRITE_WIDTH = `calc(100% - 46px)`;
const VIDEO_PREVIEW_HEIGHT_BIAS = 200;
const VIDEO_PREVIEW_CONTAINER_STYLES: CSSProperties = {
  borderRadius: `4px`,
  overflow: `hidden`,
};

function TrimVideoPanel({maxDuration = MAX_GAP, onTrim = $.noop, showMaxDurationError = false, forceTrim = false, ...props}: TrimVideoPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const param = useContainerQuery(panelRef, query);
  const dispatch = useAppDispatch();
  const panelTitle: string = param.small ? window.i18next.t('pmwjs_trim') : window.i18next.t('pmwjs_trim_video');
  const videoPreviewRef = useRef<ReactPlayer>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const id: string = uuidv4();
  const reduxStates: TrimVideoReduxState = useAppSelector((state) => {
    return state.trimPanel[TRIM_REDUX_HASHMAP_KEY];
  });
  const isPlaying: boolean = reduxStates.playing;
  const startTime: number = reduxStates.startTime ?? props.start;
  const endTime: number = reduxStates.endTime ?? props.end;
  const seekTime: number = reduxStates.seekTime ?? startTime;
  const [videoLoading, setVideoLoading] = useState<boolean>(true);
  const panelId = useRef(props.panelId ?? getUniqueString());

  const onTriggerClose = () => {
    if (forceTrim) {
      onTrimVideo();
    } else {
      if (isPlaying) {
        dispatch(togglePlaying(TRIM_REDUX_HASHMAP_KEY));
      }
      dispatch(closePanel(panelId.current));
    }
  };
  useCustomPanelClose(panelId.current, onTriggerClose);

  useEffect(() => {
    const reduxParam: TrimVideoReduxState = {
      playing: false,
      startTime: props.start,
      endTime: props.end,
      seekTime: props.start,
    };

    dispatch(
      initializeState({
        key: TRIM_REDUX_HASHMAP_KEY,
        value: reduxParam,
      } as ReduxStateSetterProps)
    );

    seekVideoTo(startTime ?? props.start);
  }, []);

  const getContent = () => {
    return (
      <PanelContent
        ref={contentContainerRef}
        className={`${styles.panel} flex-column-align-center ${
          param.small ? 'spacing-p-t-6 spacing-p-r-6 spacing-p-b-8 spacing-p-l-6' : 'spacing-p-t-8 spacing-p-r-13 spacing-p-b-8 spacing-p-l-13'
        }`}
      >
        {getVideoPreviewRow()}
        {getDurationRow()}
        {getTrimRangeSliderRow()}
      </PanelContent>
    );
  };

  const onTrimVideo = () => {
    if (isPlaying) {
      dispatch(togglePlaying(TRIM_REDUX_HASHMAP_KEY));
    }

    onTrim({
      startTime,
      endTime,
    } as TrimData);
  };

  const getFooterPrimaryActions = () => {
    return [<PanelFooterPrimaryDefaultAction key="btn-trim" customClasses="footer-trim-btn" disabled={false} text="Trim" onClick={onTrimVideo} />];
  };

  const onPlay = () => {
    dispatch(togglePlaying(TRIM_REDUX_HASHMAP_KEY));
  };

  const onVideoPlaying = (onProgressProps: OnProgressProps) => {
    let {playedSeconds} = {...onProgressProps};
    let seekOverride = false;
    if (playedSeconds > endTime) {
      playedSeconds = startTime;
      dispatch(
        setPlaying({
          key: TRIM_REDUX_HASHMAP_KEY,
          value: false,
        })
      );
      seekOverride = true;
    }
    seekVideoTo(playedSeconds, seekOverride);
  };

  const getVideoPreviewRow = () => {
    return (
      <div className={`${styles.row} ${styles.videoPreviewRow} flex-center`}>
        {param.small && !isPlaying && (
          <Icon
            className={`${styles.smallScreenPlayButton} spacing-m-r-2`}
            icon={isPlaying ? 'icon-pause' : 'icon-play-video'}
            onClick={onPlay}
            size={IconSize.SIZE_ICON_24}
            type={IconType.SECONDARY}
            shape={IconShape.CIRCLE}
          />
        )}
        {param.small && <div className={styles.videoPlayPauseOverLay} onClick={onPlay} />}
        {videoLoading && (
          <div
            className="flex-center"
            style={contentContainerRef && contentContainerRef.current ? {height: `${contentContainerRef.current.offsetHeight - VIDEO_PREVIEW_HEIGHT_BIAS}px`} : {}}
          >
            <ThreeDotsLoader distance={6} size={6} color={DOTS_COLOR.DARK} />
          </div>
        )}
        <ReactPlayer
          style={videoLoading ? {...VIDEO_PREVIEW_CONTAINER_STYLES, display: 'none'} : {...VIDEO_PREVIEW_CONTAINER_STYLES, display: 'block'}}
          ref={videoPreviewRef}
          url={props.source}
          playing={isPlaying}
          controls={false}
          volume={1}
          muted={props.isMuted}
          progressInterval={100}
          onProgress={onVideoPlaying}
          onEnded={() => {
            dispatch(
              setPlaying({
                key: TRIM_REDUX_HASHMAP_KEY,
                value: false,
              })
            );
            seekVideoTo(startTime);
          }}
          onReady={() => {
            setVideoLoading(false);
          }}
          height={contentContainerRef && contentContainerRef.current ? contentContainerRef.current.offsetHeight - VIDEO_PREVIEW_HEIGHT_BIAS : undefined}
        />
      </div>
    );
  };

  const areTimesValid = (start: number, end: number) => {
    if (start > end) {
      return false;
    }
    const gap: number = end - start;
    return MIN_GAP <= gap && gap <= maxDuration;
  };

  const invalidateStartTime = (proposedStart: number) => {
    if (areTimesValid(proposedStart, endTime)) {
      dispatch(
        setStartTime({
          key: TRIM_REDUX_HASHMAP_KEY,
          value: proposedStart,
        } as ReduxTimeSetterProps)
      );
    }
  };

  const seekVideoTo = (seekToSecond: number, renderSeek = true) => {
    if (renderSeek) {
      videoPreviewRef?.current?.seekTo(seekToSecond, 'seconds');
    }

    dispatch(
      setSeekingTime({
        key: TRIM_REDUX_HASHMAP_KEY,
        value: seekToSecond,
      })
    );
  };

  const invalidateEndTime = (propsedEnd: number) => {
    if (areTimesValid(startTime, propsedEnd)) {
      dispatch(
        setEndTime({
          key: TRIM_REDUX_HASHMAP_KEY,
          value: propsedEnd,
        } as ReduxTimeSetterProps)
      );
    }
  };

  const onTrimRangeSliderChanged = (values: number | number[]) => {
    if (typeof values !== 'number') {
      if (startTime !== values[0]) {
        seekVideoTo(values[0]);
      }
      if (endTime !== values[1]) {
        seekVideoTo(values[1]);
      }
      invalidateStartTime(values[0]);
      invalidateEndTime(values[1]);
    }
  };

  const afterTrimRangeSliderChanged = (values: number | number[]) => {
    if (typeof values !== 'number') {
      seekVideoTo(values[0]);
    }
  };

  const getTrimRangeSliderRow = () => {
    return (
      <div className={`${styles.row} spacing-m-t-3 flex-row-justify-between flex-items-center`}>
        {!param.small && (
          <Icon
            className="spacing-m-r-2"
            icon={isPlaying ? 'icon-pause' : 'icon-play-video'}
            onClick={onPlay}
            size={IconSize.SIZE_ICON_24}
            type={IconType.SECONDARY}
            shape={IconShape.CIRCLE}
          />
        )}
        <div className={styles.videoTrimContainer}>
          <TrimRangeSlider
            step={0.001}
            onAfterChange={afterTrimRangeSliderChanged}
            minGap={MIN_GAP}
            min={0}
            max={props.duration}
            value={[startTime, endTime]}
            onChange={onTrimRangeSliderChanged}
          />
          <TrimVideoThumbnailSprite
            width={VIDEO_THUMBNAIL_SPRITE_WIDTH}
            className={styles.videoSprite}
            source={props.source}
            duration={props.duration}
            scaledWidth={props.scaledWidth}
            scaledHeight={props.scaledHeight}
            height={44}
          />
          <TrimVideoProgress duration={props.duration} current={seekTime} />
        </div>
      </div>
    );
  };

  const getDurationRow = () => {
    return (
      <div className={`${styles.row} flex-row-justify-between flex-items-center ${param.small ? 'spacing-m-t-4' : 'spacing-m-t-2'}`}>
        <TimeBlock
          label={window.i18next.t('pmwjs_start_trim_time')}
          seconds={startTime}
          maxTime={endTime - MIN_GAP}
          minTime={Math.max(0, endTime - maxDuration)}
          callback={(startSeconds: number) => {
            invalidateStartTime(startSeconds);
            seekVideoTo(startSeconds);
          }}
          idDuration={`${id}-start-time`}
          alignment={TIME_BLOCK_ALIGNMENT.ALIGNLEFT}
        />

        <TimeBlock
          label={window.i18next.t('pmwjs_end_trim_time')}
          seconds={endTime}
          maxTime={Math.min(props.duration, startTime + maxDuration)}
          minTime={startTime + MIN_GAP}
          callback={(endSeconds: number) => {
            invalidateEndTime(endSeconds);
          }}
          idDuration={`${id}-end-time`}
          alignment={TIME_BLOCK_ALIGNMENT.ALIGNRIGHT}
        />
      </div>
    );
  };

  return (
    <Panel
      ref={panelRef}
      className=""
      panelHeader={<PanelHeader title={`${panelTitle}`} centerAlignTitle={param.small} onClose={onTriggerClose} showCloseButton />}
      panelContent={getContent()}
      panelFooter={<PanelFooter fullWidthPrimaryActions={param.small} border={!param.small} primaryActions={getFooterPrimaryActions()} />}
      {...(showMaxDurationError && {panelMessage: MAX_DURATION_EXCEEDED_ERROR_PROP})}
    />
  );
}

export default TrimVideoPanel;
